import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { TraumaHistoryFormValues } from "./interface";
import useGetTraumaHistory from "hooks/api/client/useGetTraumaHistory";
import useUpsertTraumaHistory from "hooks/api/client/useUpsertTraumaHistory";

const EMPTY_VALUES: TraumaHistoryFormValues = {
  hasTraumaHistory: "",
};

export const TraumaHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { traumaHistory, refetchTraumaHistory } = useGetTraumaHistory(clientIdNum);

  const { upsertTraumaHistoryMutation } = useUpsertTraumaHistory({
    onSuccess: () => {
      refetchTraumaHistory();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<TraumaHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<TraumaHistoryFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (traumaHistory) {
      const mapped: TraumaHistoryFormValues = {
        hasTraumaHistory: traumaHistory.hasTraumaHistory || "",
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [traumaHistory]);

  const handleChange =
    (field: keyof TraumaHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertTraumaHistoryMutation({
      clientId: clientIdNum,
      payload: { hasTraumaHistory: formValues.hasTraumaHistory || null },
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues);

  return (
    <SectionWrapper
      title="Trauma History"
      description="Past traumatic experiences that may influence the client's emotional regulation, behavioral responses, and overall engagement in treatment."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default TraumaHistory;
